import React, { useState } from 'react';

import {
  CurrencyInputWithConfirm,
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  RadioOptionsWithConfirm,
} from 'shared-components';
import { countries as countriesData } from 'platform-data';
import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import { useDispatch } from 'react-redux';
import { savePreCasInterview } from '../../../../store/student/actions';
import { createUpdatePCIFieldAction } from '../../../../store/student/preCasInterview/actions';
import { Pages } from '../../../../store/student/preCasInterview/types';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';

export interface IProps {
  complete: boolean;
  currentStep: number;
  steps: number;
  workWhileStudy?: boolean;
  maxWorkHoursPerWeek: string;
  whyWorkWhileStudy: string;
  whyWorkWhileStudyOther: string;
  affordCourseAndLivingWithoutWorking?: boolean;
  affordCourseAndLivingWithoutWorkingNo: string;
  completeDegreeWorkCountry: string;
  expectedSalary?: number;
  furtherStudyInUk?: boolean;
  furtherStudyCareerPlans: string;
  pageConfig: any;
}

type Props = IProps;

function InterviewQPage3(props: Props) {
  const [workWhileStudyError, _setWorkWhileStudyError] = useState('');
  const [maxWorkHoursPerWeekError, _setMaxWorkHoursPerWeekError] = useState('');
  const [whyWorkWhileStudyError, _setWhyWorkWhileStudyError] = useState('');
  const [whyWorkWhileStudyOtherError, _setWhyWorkWhileStudyOtherError] = useState('');
  const [affordCourseAndLivingWithoutWorkingError, _setAffordCourseAndLivingWithoutWorkingError] = useState('');
  const [affordCourseAndLivingWithoutWorkingNoError, _setAffordCourseAndLivingWithoutWorkingNoError] = useState('');
  const [completeDegreeWorkCountryError, setCompleteDegreeWorkCountryError] = useState('');
  const [countryWorkSuggestions, setCountryWorkSuggestions] = useState([]);
  const [expectedSalaryError, _setExpectedSalaryError] = useState('');
  const [furtherStudyInUkError, _setFurtherStudyInUkError] = useState('');
  const [furtherStudyCareerPlansError, _setFurtherStudyCareerPlansError] = useState('');

  const countries = [...new Set(countriesData.map(country => country.countryName))];

  const dispatch = useDispatch();
  // const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  const onNext = () => {
    dispatch(savePreCasInterview(Pages.PAGE3));
  };
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdatePCIFieldAction(fieldName, value, refValue));

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();
  // });

  const onMaxHoursChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if ((Number(value) && value.length < 3) || value === '') {
      onChange(Q.MAX_WORK_HOURS_PER_WEEK, value);
    }
  };

  const onWorkCountryChange = evt => {
    const value = evt.currentTarget.value || '';
    const suggestions = countries.filter(country => country.toLowerCase().includes(value.toLowerCase()));

    setCountryWorkSuggestions(suggestions);
    onChange(Q.COMPLETE_DEGREE_WORK_COUNTRY, value);
  };

  const onBlurWorkCountry = event => {
    const value = (event && event.currentTarget && event.currentTarget.value.trim()) || '';
    const completeDegreeWorkCountryError = countries.includes(value) ? '' : 'Please enter a country';
    setCompleteDegreeWorkCountryError(completeDegreeWorkCountryError);
    onChange(Q.COMPLETE_DEGREE_WORK_COUNTRY, value);
  };

  const onFocusWorkCountry = () => {
    if (completeDegreeWorkCountryError !== '') {
      setCompleteDegreeWorkCountryError('');
    }
  };

  return (
    <QuestionPage
      complete={props.complete}
      currentStep={props.currentStep}
      steps={props.steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Pre-CAS Questionnaire'}
    >
      <RadioOptionsWithConfirm
        key={Q.WORK_WHILE_STUDY}
        label="Do you plan to work while you study?"
        errorText={workWhileStudyError}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: props.workWhileStudy === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: props.workWhileStudy === false,
          },
        ]}
        isInline={true}
        inputHelperText={props.pageConfig.WorkWhileStudy?.helpText}
        onClick={question => onChange(Q.WORK_WHILE_STUDY, question.label === 'Yes')}
      />

      {props.workWhileStudy === true && (
        <>
          <InputWithConfirm
            key={Q.MAX_WORK_HOURS_PER_WEEK}
            value={props.maxWorkHoursPerWeek}
            errorText={maxWorkHoursPerWeekError}
            label={'Do you know the maximum number of hours per week you are permitted to work on a student visa?'}
            subText={'Enter a two-digit number below'}
            onChange={onMaxHoursChange}
          />

          <RadioOptionsWithConfirm
            label="Why are you planning to work part-time in the UK while you study?"
            errorText={whyWorkWhileStudyError}
            questions={[
              {
                char: '1',
                label: 'Extra money for recreation',
                selected: props.whyWorkWhileStudy === 'Extra money for recreation',
              },
              {
                char: '2',
                label: 'For experience in my industry',
                selected: props.whyWorkWhileStudy === 'For experience in my industry',
              },
              {
                char: '3',
                label: 'To experience working in the UK',
                selected: props.whyWorkWhileStudy === 'To experience working in the UK',
              },
              {
                char: '4',
                label: 'Other',
                selected: props.whyWorkWhileStudy === 'Other',
              },
            ]}
            onClick={question => onChange(Q.WHY_WORK_WHILE_STUDY, question.label)}
          />

          {props.whyWorkWhileStudy === 'Other' && (
            <InputWithConfirm
              key={Q.WHY_WORK_WHILE_STUDY_OTHER}
              value={props.whyWorkWhileStudyOther}
              errorText={whyWorkWhileStudyOtherError}
              label={'Please explain'}
              inputHelperText={props.pageConfig.WorkWhileStudyReason?.helpText}
              onChange={evt => onChange(Q.WHY_WORK_WHILE_STUDY_OTHER, evt.currentTarget.value)}
            />
          )}

          <RadioOptionsWithConfirm
            key={Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING}
            label="Are you able to afford your course fees and living expenses without working in the UK?"
            errorText={affordCourseAndLivingWithoutWorkingError}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: props.affordCourseAndLivingWithoutWorking === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: props.affordCourseAndLivingWithoutWorking === false,
              },
            ]}
            isInline={true}
            inputHelperText={props.pageConfig.CourseAndLivingWithoutWorking?.helpText}
            onClick={question => onChange(Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING, question.label === 'Yes')}
          />

          {props.affordCourseAndLivingWithoutWorking === false && (
            <InputWithConfirm
              key={Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING_NO}
              value={props.affordCourseAndLivingWithoutWorkingNo}
              errorText={affordCourseAndLivingWithoutWorkingNoError}
              label={'Please explain'}
              inputHelperText={props.pageConfig.CourseAndLivingWithoutWorkingReason?.helpText}
              onChange={evt => onChange(Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING_NO, evt.currentTarget.value)}
            />
          )}
        </>
      )}

      <AutocompleteInputWithConfirm
        id="complete-degree-work-country"
        label="What country do you plan to work in when you complete your degree?"
        onChange={onWorkCountryChange}
        value={props.completeDegreeWorkCountry}
        showErrorText={!!completeDegreeWorkCountryError}
        errorText={completeDegreeWorkCountryError}
        suggestions={countryWorkSuggestions}
        onBlur={onBlurWorkCountry}
        onFocus={onFocusWorkCountry}
      />

      <CurrencyInputWithConfirm
        id="expectedSalary"
        value={props.expectedSalary}
        errorText={expectedSalaryError}
        onChange={event => {
          const value = Number(event.currentTarget.value) || 0;
          onChange(Q.EXPECTED_SALARY, value);
        }}
        label="Once you graduate, how much do you expect to earn per year in your home country?"
        subText={'Please insert an approximate salary figure in GBP (per year)'}
      />

      <RadioOptionsWithConfirm
        label="Do you plan to continue on to further study in the UK after your course is complete?"
        errorText={furtherStudyInUkError}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: props.furtherStudyInUk === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: props.furtherStudyInUk === false,
          },
        ]}
        isInline={true}
        onClick={question => onChange(Q.FURTHER_STUDY_IN_UK, question.label === 'Yes')}
      />

      {props.furtherStudyInUk === true && (
        <InputWithConfirm
          key={Q.FURTHER_STUDY_CAREER_PLANS}
          value={props.furtherStudyCareerPlans}
          errorText={furtherStudyCareerPlansError}
          label={'Explain how this fits into your career plans'}
          inputHelperText={props.pageConfig.FurtherStudyCareerPlan?.helpText}
          onChange={evt => onChange(Q.FURTHER_STUDY_CAREER_PLANS, evt.currentTarget.value)}
        />
      )}
    </QuestionPage>
  );
}

export default InterviewQPage3;
